import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { calculateDistance } from "@/hooks/useLocationSearch";
import { supabase } from "@/integrations/supabase/client";
import { mayCheckDevicePositionQuietly } from "@/lib/quietLocationRefresh";

interface CafeWithLocation {
  id: string;
  name: string;
  slug: string;
  latitude: number | null;
  longitude: number | null;
  cover_image_url: string | null;
  neighborhood: string | null;
}

export interface NearbyCafe extends CafeWithLocation {
  distanceMeters: number;
}

// ~200 meters = ~0.12 miles
const PROXIMITY_THRESHOLD_MILES = 0.12;

// Bounding box radius in degrees (~200m)
const BOUNDING_BOX_DEGREES = 0.002;

// Debounce check to avoid rapid re-checks
const CHECK_DEBOUNCE_MS = 10000;

// Dismiss suppression duration (30 minutes)
const DISMISS_DURATION_MS = 30 * 60 * 1000;

const SESSION_STORAGE_KEY = "proximity-dismissed-cafes";
const SESSION_RESOLVED_KEY = "proximity-prompt-resolved";

function getDismissedCafeIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function setDismissedCafeIdsStorage(ids: Set<string>) {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Ignore storage errors
  }
}

function isDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_RESOLVED_KEY) !== null;
  } catch {
    return false;
  }
}

export function useProximityCheckIn() {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();

  const [nearbyCafes, setNearbyCafes] = useState<NearbyCafe[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [dismissedCafeIds, setDismissedCafeIds] = useState<Set<string>>(getDismissedCafeIds);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isFirstCheckIn, setIsFirstCheckIn] = useState(false);
  const [debugMessages, setDebugMessages] = useState<string[]>([]);

  const pushDebug = useCallback((msg: string) => {
    const stamped = `${new Date().toLocaleTimeString()} ${msg}`;
    setDebugMessages((prev) => [...prev.slice(-49), stamped]);
  }, []);

  const lastCheckTimeRef = useRef<number>(0);
  const checkInProgressRef = useRef(false);

  // Sync dismissedCafeIds to sessionStorage
  useEffect(() => {
    setDismissedCafeIdsStorage(dismissedCafeIds);
  }, [dismissedCafeIds]);

  const checkProximity = useCallback(async () => {
    if (isDismissedThisSession()) {
      console.log("[useProximityCheckIn] suppressed: session resolved");
      pushDebug("suppressed: session resolved");
      return;
    }
    const now = Date.now();
    if (now - lastCheckTimeRef.current < CHECK_DEBOUNCE_MS) return;
    if (checkInProgressRef.current) return;
    if (!user) return;
    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported");
      return;
    }

    checkInProgressRef.current = true;
    // This check runs on its own, so it must never be what shows the location
    // pop up: only when access is already granted, or the person already uses
    // "Current Location" on Browse.
    if (!(await mayCheckDevicePositionQuietly())) {
      checkInProgressRef.current = false;
      pushDebug("skipped: location not shared yet");
      return;
    }
    lastCheckTimeRef.current = now;
    setIsChecking(true);
    setLocationError(null);
    console.log("[useProximityCheckIn] proximity check triggered");
    pushDebug("proximity check triggered");

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              reject(new Error("permission_denied"));
            } else {
              reject(error);
            }
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );
      });

      const accuracy = position.coords.accuracy;
      console.log("[useProximityCheckIn] GPS accuracy:", accuracy, "meters");
      pushDebug(`GPS accuracy: ${accuracy}m`);

      // If GPS is too imprecise to distinguish neighboring cafes, skip
      if (accuracy > 150) {
        console.log("[useProximityCheckIn] accuracy too low:", accuracy);
        console.log("[useProximityCheckIn] Accuracy too low, skipping proximity check");
        pushDebug(`accuracy too low: ${accuracy}`);
        return;
      }

      const userLat = position.coords.latitude;
      const userLng = position.coords.longitude;

      // Direct database query with bounding box (~200m)
      const { data: cafes, error: queryError } = await supabase
        .from("cafes")
        .select("id, name, slug, latitude, longitude, cover_image_url, neighborhood")
        .eq("is_active", true)
        .gte("latitude", userLat - BOUNDING_BOX_DEGREES)
        .lte("latitude", userLat + BOUNDING_BOX_DEGREES)
        .gte("longitude", userLng - BOUNDING_BOX_DEGREES)
        .lte("longitude", userLng + BOUNDING_BOX_DEGREES);

      if (queryError) {
        console.error("[useProximityCheckIn] DB query error:", queryError.message);
        return;
      }

      if (!cafes || cafes.length === 0) {
        setNearbyCafes([]);
        return;
      }

      // Filter to ~200m threshold
      const nearby: NearbyCafe[] = [];

      for (const cafe of cafes) {
        if (cafe.latitude == null || cafe.longitude == null) continue;
        const distanceMiles = calculateDistance(userLat, userLng, cafe.latitude, cafe.longitude);
        if (distanceMiles < PROXIMITY_THRESHOLD_MILES) {
          if (dismissedCafeIds.has(cafe.id)) continue;
          nearby.push({ ...cafe, distanceMeters: Math.round(distanceMiles * 1609.34) });
        }
      }

      if (nearby.length === 0) {
        console.log("[useProximityCheckIn] no cafés within threshold");
        pushDebug("no cafés within threshold");
      }

      // Sort by distance
      nearby.sort((a, b) => a.distanceMeters - b.distanceMeters);

      // Check if this is the user's first ever check-in
      if (nearby.length > 0) {
        const { count } = await supabase
          .from("check_ins")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("is_complete", true)
          .is("followup_checkin_id", null);
        setIsFirstCheckIn(count === 0);
      }

      // Filter out cafes with a recent complete check-in (within 1 hour)
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const filtered: NearbyCafe[] = [];

      for (const cafe of nearby) {
        const { data: recentCheckIn } = await supabase
          .from("check_ins")
          .select("id")
          .eq("cafe_id", cafe.id)
          .eq("user_id", user.id)
          .gte("checked_in_at", oneHourAgo)
          .limit(1)
          .maybeSingle();

        if (!recentCheckIn) {
          filtered.push(cafe);
        } else {
          console.log("[useProximityCheckIn] Recent check-in exists for", cafe.name, ", suppressing");
        }
      }

      if (filtered.length > 0) {
        console.log("[useProximityCheckIn] proximity prompt firing for", filtered[0].name);
        pushDebug(`proximity prompt firing for ${filtered[0].name}`);
      }
      setNearbyCafes(filtered);
    } catch (error: any) {
      if (error.message === "permission_denied") return;

      let msg = "Unknown location error";
      if (error?.code === 1) msg = "Location permission denied";
      else if (error?.code === 2) msg = "Location unavailable";
      else if (error?.code === 3) msg = "Location request timed out";
      else if (error?.message) msg = error.message;

      console.error("[useProximityCheckIn]", msg);
      setLocationError(msg);
    } finally {
      setIsChecking(false);
      checkInProgressRef.current = false;
    }
  }, [user, dismissedCafeIds, pushDebug]);

  // Trigger check when user lands on Browse page
  useEffect(() => {
    if (location.pathname !== "/browse") return;
    if (authLoading || !user) return;

    const timer = setTimeout(() => {
      checkProximity();
    }, 3000);

    return () => clearTimeout(timer);
  }, [authLoading, user, checkProximity, location.pathname]);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(SESSION_RESOLVED_KEY, Date.now().toString());
    setNearbyCafes((current) => {
      if (current.length > 0) {
        setDismissedCafeIds((prev) => {
          const next = new Set(prev);
          for (const c of current) next.add(c.id);
          return next;
        });
      }
      return [];
    });
  }, []);

  // After any completed check-in, suppress proximity prompt for all cafes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("proximity-checkin-suppress")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "check_ins",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new?.is_complete === true) {
            sessionStorage.setItem(SESSION_RESOLVED_KEY, Date.now().toString());
            setNearbyCafes([]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Backward compat: single nearest cafe
  const nearbyCafe = nearbyCafes.length > 0 ? nearbyCafes[0] : null;

  return {
    nearbyCafe,
    nearbyCafes,
    isChecking,
    isFirstCheckIn,
    locationError,
    dismiss,
    debugMessages,
  };
}
